import { useCallback, useEffect, useRef, useState } from "react";
import { fetchAccessToken, logout } from "@origin-digital/event-dispatcher";
import { shouldAttemptAuthFetch } from "@origin-digital/tal-client-common";
import { logger } from "@origin-digital/reporting-client";
import {
  fetchTokenShieldValid,
  pollIntervalExceeded,
  TOKEN_SHIELD_POLL_INTERVAL,
} from "./tokenShieldService";

type tokenState = {
  token?: string;
  valid?: boolean;
  lastPollTime?: Date;
  pollTimeout?: NodeJS.Timeout;
};
/**
 * Hook used to manage polling of Tokenshield API, and handle logging out of users with invalid tokens
 *
 * This makes extensive use of useRef to maintain polling and state logic manually to get fine grained control outside of the react lifecycle
 *
 * Tokenshield service manages the polling logic, and uses daxi cache, and localstorage to avoid duplicate polls on component render, or when multiple TAL instances are running
 * (Ie. multiple tabs are open)
 *
 * Logical flow is as follows:
 * - On mount:
 *    Run poll function.
 *
 * - On Poll:
 *    Register timeout for next poll at POLL_INTERVAL
 *    Check stored tokenshield value from local storage (handled in service)
 *    Poll tokenshield
 *    Trigger shouldLogout to make sure logout is run immediately
 *
 * - On unmount:
 *    Cancel any existing timeouts for polling
 *
 *  - on should logout:
 *      calls logout function, utilising mesh to ensure logout works across desktop and mobile.
 *
 * **/
export const useTokenShield = () => {
  // Using object to manually control Re-renders / useEffects
  const tokenState = useRef<tokenState>({
    token: undefined,
    valid: undefined,
    lastPollTime: undefined,
    pollTimeout: undefined,
  });
  // Manually trigger logout check, to avoid race conditions, by setting this
  const [shouldCheckLogout, setShouldCheckLogout] = useState<boolean>(false);
  const [shouldPoll, setShouldPoll] = useState<boolean>(false);

  /* Function to handle fetching token. Setup with useCallback so it can be reused in useEffect*/
  const setTokenFromPlatform = useCallback(async () => {
    if (!shouldAttemptAuthFetch()) {
      // Neither this page's manifest requires login, nor does the visitor
      // already have a session cookie (see shouldAttemptAuthFetch()'s doc
      // comment) — fetchAccessToken's underlying silent Auth0 check (see
      // spaAuthHelpers.ts) has no reason to succeed here. Skip it entirely
      // rather than polling a call that's guaranteed to fail; checked live
      // on every poll cycle, not just once.
      tokenState.current.token = undefined;
      return;
    }
    try {
      const result = await fetchAccessToken({});
      const fetchedToken = result?.accessToken;
      if (tokenState.current.token !== fetchedToken) {
        tokenState.current.token = fetchedToken;
        setShouldPoll(true);
      }
    } catch {
      logger.debug(
        "Failed to fetch token from platform, setting token to undefined"
      );
      tokenState.current.token = undefined;
    }
  }, []);

  /* Function to handle Polling Tokenshield. UseRef used because we never want to use this to trigger useEffect*/
  const pollTokenShield = useRef(async () => {
    tokenState.current.pollTimeout = setTimeout(
      pollTokenShield.current,
      TOKEN_SHIELD_POLL_INTERVAL
    );
    setShouldPoll(false);
    if (!tokenState.current.token) {
      await setTokenFromPlatform();
    }
    if (
      tokenState.current.token &&
      pollIntervalExceeded(tokenState.current.lastPollTime).shouldPoll
    ) {
      const isValid = await fetchTokenShieldValid();
      tokenState.current.lastPollTime = new Date();
      tokenState.current.valid = isValid;
      if (!isValid) {
        setShouldCheckLogout(true);
      }
    }
  });

  /* Setup polling. This also covers the initial fetch: pollTokenShield.current()
   * checks `!tokenState.current.token` and calls setTokenFromPlatform() itself,
   * so a separate "fetch on mount" effect isn't needed — there used to be one
   * here, calling setTokenFromPlatform() a second, redundant time on every
   * mount (both effects fire on initial render regardless of dependencies). */
  useEffect(() => {
    pollTokenShield.current();
    return () => clearTimeout(tokenState.current.pollTimeout);
  }, [shouldPoll]);

  /*Logout user if token is ever invalid*/
  useEffect(() => {
    if (tokenState.current.token && tokenState.current.valid === false) {
      // use eventDispatcher/Mesh event to ensure logout experience works consistently across native and web
      logout();
      // These may be unnecessary, since logout will redirect the user and unmount the component, but just in case.
      tokenState.current = {
        ...tokenState.current,
        token: undefined,
        valid: undefined,
      };
    }
    setShouldCheckLogout(false);
  }, [tokenState, shouldCheckLogout]);

  return { ...tokenState };
};
